export interface ArcadeLocation {
  id: string;
  venue?: string;
  city: string;
  country: string;
  // ISO-3166 alpha-2 (플래그 CDN·?country= 딥링크·국가 그룹핑용). 데이터 provisional.
  countryCode?: string;
  address?: string;
  lat?: number;
  lng?: number;
  // 파트너 예약 딥링크 경로 조각(sandboxvr.com/{slug}/booking). 없으면 generic 폴백. provisional.
  slug?: string;
  // Opening Soon(예약 비활성). 데이터 이름의 "(Coming Soon)" 접미사에서 유도. provisional.
  soon?: boolean;
  phone?: string;
  hours?: string;
  website?: string;
  mapUrl?: string;
  _source?: string;
}

export interface ArcadePartner {
  id: string;
  name: string;
  website: string;
  logoComponent: string | null;
  description: string;
  products: string[];
  locationsUrl?: string;
  // 예약 딥링크 베이스(+ /{slug}/booking). slug 없을 땐 bookingUrl generic 폴백. provisional.
  bookingBase?: string;
  bookingUrl?: string;
  // 상시 개방 매장이 아니라 문의·견적 기반 파트너(오피스/데모 업체). CTA 를 "Book ..." 대신
  // "Contact"(파트너 홈 링크)로 바꾼다. 사용자가 예약 가능한 매장으로 오인해 헛걸음하는 것 방지.
  //
  // granularity 규칙: CTA 에 영향 주는 상태는 **venue 고유면 `ArcadeLocation`**(예: `soon`),
  // **파트너 전체면 여기**. 한 파트너 안에서 지점별로 갈리면(쇼룸+예약제 창고) location 쪽에
  // 같은 이름 필드를 추가하고 `loc.inquiryOnly ?? p.inquiryOnly` 로 해소할 것.
  inquiryOnly?: boolean;
  locations: ArcadeLocation[];
}

export interface ArcadeDataFile {
  lastUpdatedAt: string | null;
  partners: ArcadePartner[];
}

export interface FlatLocation extends ArcadeLocation {
  partnerId: string;
  partnerName: string;
  partnerWebsite: string;
  partnerLogoComponent: string | null;
  // 예약 CTA(bookCta)가 FlatLocation 하나로 해소되도록 파트너 예약 필드를 평탄화해 실어둔다.
  partnerBookingBase?: string;
  partnerBookingUrl?: string;
  partnerInquiryOnly?: boolean;
}

export function flattenLocations(data: ArcadeDataFile): FlatLocation[] {
  return data.partners.flatMap((p) =>
    p.locations.map((loc) => ({
      ...loc,
      partnerId: p.id,
      partnerName: p.name,
      partnerWebsite: p.website,
      partnerLogoComponent: p.logoComponent,
      partnerBookingBase: p.bookingBase,
      partnerBookingUrl: p.bookingUrl,
      partnerInquiryOnly: p.inquiryOnly,
    })),
  );
}

// Default map view: roughly centered on Europe so all known venues fit.
export const DEFAULT_MAP_CENTER = { lat: 30, lng: 10 };
export const DEFAULT_MAP_ZOOM = 2;

// ── 파트너 색 단일 정본 (§8, Carbon 토큰, 확정 2026-07-03) ──────────────────
// ArcadeMap 마커 · LocationList dot · (다중 파트너 시) 범례/섹션헤더가 공유한다.
// ⚠ 이 hex 를 유일 원천으로 둔다. 컴포넌트에서 별도 색 정의 금지.
export const PARTNER_COLOR: Record<string, string> = {
  "sandbox-vr": "#0F62FE", // blue-60
  hologate: "#8A3FFC", // purple-60
  "vex-solutions": "#007D79", // teal-60
  "levels-whitehaven": "#EB6200", // orange-50
  eventop: "#D02670", // magenta-60
};
export const PARTNER_COLOR_FALLBACK = "#0F62FE"; // blue-60

export function partnerColor(partnerId: string): string {
  return PARTNER_COLOR[partnerId] ?? PARTNER_COLOR_FALLBACK;
}

// ── 국가 플래그 (bHaptics CDN) ────────────────────────────────────────────────
// CDN 예외: GB 는 GBR.svg 로 호스팅됨(GB.svg 없음). cc 는 대문자 ISO-2.
export const FLAG_CODE: Record<string, string> = { GB: "GBR" };
export function flagUrl(cc: string): string {
  const code = FLAG_CODE[cc] ?? cc;
  return `https://www.bhaptics.com/flags/l/${code}.svg`;
}

export interface CountryOption {
  code: string; // ISO-2 (대문자)
  name: string; // 데이터의 country 명(하드코딩 금지 — 매장 데이터에서 파생)
  count: number;
}

// 매장 보유 국가만, countryCode 로 그룹. 많은 순 → 이름순. (프로토 14개국 하드코딩 대체)
export function countriesWithVenues(locs: FlatLocation[]): CountryOption[] {
  const map = new Map<string, CountryOption>();
  for (const loc of locs) {
    const code = loc.countryCode?.toUpperCase();
    if (!code) continue;
    const cur = map.get(code);
    if (cur) cur.count++;
    else map.set(code, { code, name: loc.country, count: 1 });
  }
  return Array.from(map.values()).sort(
    (a, b) => b.count - a.count || a.name.localeCompare(b.name),
  );
}

// 파트너 탭용 옵션. countriesWithVenues 의 파트너 판(版) — 주어진 venue 집합에 매장이 있는
// 파트너만, 그 집합 기준 카운트로. 순서는 데이터(partners 배열) 순서를 유지한다.
//
// ⚠️ `ArcadePartner` 를 그대로 넘기지 않는 이유: locations 를 국가 범위로 갈아끼운 사본을
// 넘기면 "partner.locations 가 전체인지 범위분인지" 를 읽는 쪽이 알 수 없다(타입이 거짓말).
// count 로 이름을 붙여 범위를 명시한다.
export interface PartnerOption {
  id: string;
  name: string;
  count: number;
}

export function partnersWithVenues(
  partners: ArcadePartner[],
  locs: FlatLocation[],
): PartnerOption[] {
  const counts = new Map<string, number>();
  for (const loc of locs)
    counts.set(loc.partnerId, (counts.get(loc.partnerId) ?? 0) + 1);
  return partners
    .map((p) => ({ id: p.id, name: p.name, count: counts.get(p.id) ?? 0 }))
    .filter((p) => p.count > 0);
}

// ── Geo helpers (always-Nearest 정렬 / "Use my location") ────────────────────
export interface LatLng {
  lat: number;
  lng: number;
}

// Haversine 거리(km). 핀·정렬용 근사로 충분. 순수함수라 useMemo 안전.
export function haversineKm(a: LatLng, b: LatLng): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

function hasCoords(
  loc: FlatLocation,
): loc is FlatLocation & { lat: number; lng: number } {
  return typeof loc.lat === "number" && typeof loc.lng === "number";
}

// 국가 매장 좌표 평균(중심). userLoc 없을 때 always-Nearest 정렬 기준점.
// 국가별 center/zoom 을 데이터에 두지 않고 런타임 파생(가이드 §4-2).
export function countryCentroid(locs: FlatLocation[]): LatLng | null {
  const pts = locs.filter(hasCoords);
  if (!pts.length) return null;
  const sum = pts.reduce(
    (acc, l) => ({ lat: acc.lat + l.lat, lng: acc.lng + l.lng }),
    { lat: 0, lng: 0 },
  );
  return { lat: sum.lat / pts.length, lng: sum.lng / pts.length };
}

// from 지점 기준 가장 가까운 매장. 정밀좌표 얻었을 때 nearest 프레이밍/국가 전환용.
export function nearestVenue(
  locs: FlatLocation[],
  from: LatLng,
): FlatLocation | null {
  let best: FlatLocation | null = null;
  let bestKm = Infinity;
  for (const loc of locs) {
    if (!hasCoords(loc)) continue;
    const km = haversineKm(from, loc);
    if (km < bestKm) {
      bestKm = km;
      best = loc;
    }
  }
  return best;
}

// ── 예약 CTA / 길찾기 (프로토 bookCta ~801 / dirUrl ~973) ─────────────────────
// 라벨은 i18n 대상이라 여기선 kind 만 정하고 문자열은 호출처(t())가 만든다(순수 유지).
export type BookCta =
  | { kind: "book"; href: string; ext: false } // 매장별 딥링크 or generic 폴백
  | { kind: "bookOn"; href: string; ext: true } // 파트너 홈으로(외부)
  | { kind: "contact"; href: string; ext: boolean };

// soon(Opening Soon) 매장은 CTA 없음 → null. 프로토 4분기(딥링크>generic>파트너홈>Contact).
export function bookCta(loc: FlatLocation): BookCta | null {
  if (loc.soon) return null;
  // "#" 은 링크 없음 센티넬. 아래 분기들이 같은 판정을 반복하지 않도록 한 번만 해소한다.
  const site =
    loc.partnerWebsite && loc.partnerWebsite !== "#"
      ? loc.partnerWebsite
      : null;
  // inquiryOnly 파트너는 "예약"할 세션이 없다(상시 개방 매장이 아니라 견적·문의 기반 업체).
  // **무조건** 예약 분기보다 먼저 — website 유효성과 conjoin 하면 사이트가 없는 문의형 파트너가
  // 예약 waterfall 로 흘러내려 "Book a session" 이 찍힌다(플래그가 막으려던 바로 그 상황).
  if (loc.partnerInquiryOnly)
    return { kind: "contact", href: site ?? "#", ext: site != null };
  if (loc.partnerBookingBase && loc.slug)
    return {
      kind: "book",
      href: `${loc.partnerBookingBase}/${loc.slug}/booking`,
      ext: false,
    };
  if (loc.partnerBookingUrl)
    return { kind: "book", href: loc.partnerBookingUrl, ext: false }; // generic(위치 선택기, 여전히 기능)
  if (site) return { kind: "bookOn", href: site, ext: true };
  return { kind: "contact", href: "#", ext: false };
}

// 길찾기 딥링크는 이름-검색이 아니라 정확 lat/lng — 동명 매장 오폭발 방지(가이드 §4-3).
export function dirUrl(loc: FlatLocation): string | null {
  if (typeof loc.lat !== "number" || typeof loc.lng !== "number") return null;
  return `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}`;
}
